import tpl from './button.tpl.html';
import linkTpl from './link.tpl.html';
import './button.component.scss';
import {noop} from "../../../app/common/common.utils";


/**
 * Choices for the button size:
 *
 * - normal: normal buttons
 * - minimal: smaller buttons
 * - large: bigger buttons
 * - massive: slightly bigger buttons than large, for confirmation buttons, main actions etc.
 * - icon: circle icon button, with this button the pixelSize argument must also be given
 *
 * @type {{MINIMAL: Symbol, NORMAL: Symbol, LARGE: Symbol, MASSIVE: Symbol, ICON: Symbol}}
 */
const SIZE_CHOICES = {
  MINIMAL: Symbol('minimal'),
  NORMAL: Symbol('normal'),
  LARGE: Symbol('large'),
  MASSIVE: Symbol('massive'),
  ICON: Symbol('icon')
};


/**
 * Choices for the button color.
 *
 * @type {{NONE: Symbol, GREEN: Symbol, WHITEGREEN: Symbol, BLUE: Symbol, WHITEBLUE: Symbol, GRAY: Symbol}}
 */
const COLOR_CHOICES = {
  NONE: Symbol('none'),
  GREEN: Symbol('green'),
  WHITEGREEN: Symbol('whitegreen'),
  BLUE: Symbol('blue'),
  WHITEBLUE: Symbol('whiteblue'),
  RED: Symbol('red'),
  WHITERED: Symbol('whitered'),
  GRAY: Symbol('gray'),
};


/**
 * Get the color of the button based on its light and highlight status.
 * @param {boolean} light True if the button is on a dark background and should be light.
 * @param {boolean} highlight True if the button should be highlighted from others with a blue color.
 * @param {boolean} error True if the button should be reddish for error
 * @param {boolean} gray True if button should be grayish for unimportant buttons.
 * @return {Symbol}
 */
function getColor(light, highlight, error, gray=false) {
  if (error && light) {
    return COLOR_CHOICES.WHITERED;
  }
  if (error) {
    return COLOR_CHOICES.RED;
  }
  else if (light && !highlight) {
    return COLOR_CHOICES.WHITEGREEN;
  }
  else if (!light && highlight) {
    return COLOR_CHOICES.BLUE;
  }
  else if (light && highlight) {
    return COLOR_CHOICES.WHITEBLUE;
  }
  else if (gray) {
    return COLOR_CHOICES.GRAY;
  }
  else {
    return COLOR_CHOICES.GREEN;
  }
}


class MDCButtonComponent {
  constructor($element) {

    this.$element = $element;

    this.SizeChoices = SIZE_CHOICES;
    this.ColorChoices = COLOR_CHOICES;

    /**
     * Icon to show in the button
     * @type {string}
     */
    this.icon = null;

    /**
     * Size of the button, one of the size choices above
     * @type {Symbol}
     */
    this.size = SIZE_CHOICES.MINIMAL;

    /**
     * Color mode of the button, one of the choices above
     * @type {string}
     */
    this.colorMode = COLOR_CHOICES.NONE;

    /**
     * Is this button disabled?
     * @type {boolean}
     */
    this.isDisabled = false;

    /**
     * HTML type of the button element.
     * @type {string}
     */
    this.type = '';

    /**
     * The pixel size, only used if button size is ICON.
     * @type {number}
     */
    this.pixelSize = 0;

    /**
     * Does the button have a thick-ish border (as used in icon buttons)?
     * @type {boolean}
     */
    this.thickBorder = false;

    /**
     * Does the button have no border at all? Overrides thickBorder
     * @type {boolean}
     */
    this.noBorder = false;

    /**
     * Callback to execute when button is clicked, if any.
     *
     * Not executed if button is disabled.
     *
     * @type {?function()}
     */
    this.clickAction = null;

    /**
     * Link for a-element href. If link is provided element will use link based template.
     *
     * @type {?string}
     */
    this.link = null;

    /**
     * Possible value for rel attribute for a-element.
     *
     * @type {?string}
     */
    this.linkRel = null;

    /**
     * Target of the link. Can use the defaul HTML5 targets: _self, _blank, _parent, _top.
     *
     * @type {?string}
     */
    this.target = '_self';

    this._customStyles = {};
    this._buttonElem = null;
    this._ripple = null;
    this._hoverListener = () => this._ripple.layout();

    // Classes to apply with ng-class
    this._classes = {};
  }

  $onInit() {
    if (this.type === '') {
      this.type = 'button';
    }

    if (this.link != null) {
      this.buttonType = 'a';
    }
    else {
      this.buttonType = 'button';
    }

    this.colorMode = this.getButtonColor();

    // Insert ripple to the actual clickable element
    this._buttonElem = this.$element.find(this.buttonType)[0];
    
    // Mobile safari fix: empty ontouch handler allows ripple to work.
    this._buttonElem.addEventListener('touchstart', noop, false);
  
    // MDCRipple doesn't work on IE10
    try {
      this._ripple = new window.mdc.MDCRipple(this._buttonElem);
    }
    catch(e) {
      // pass
    }

    // When button is hovered, re-layout ripple because it might be broken due to having display: none. Check this
    // issue:
    // https://github.com/material-components/material-components-web/issues/774#issuecomment-315082021
    this._buttonElem.addEventListener('mouseover', this._hoverListener, false);


    if (this.size === SIZE_CHOICES.ICON) {
      this._customStyles = {
        width: `${this.pixelSize}px`,
        height: `${this.pixelSize}px`,
        padding: 0,
        'border-radius': '50%'
      };
    }

    this._setClasses();
    this._setAttrs();
  }

  $onChanges() {
    this._setClasses();
  }

  $onDestroy() {
    this._buttonElem.removeEventListener('mouseover', this._hoverListener);
  }

  getButtonColor() {
    throw new Error('Implement the getButtonColor in child class');
  }

  _onClick() {
    if (!this.isDisabled && this.clickAction != null) {
      this.clickAction();
    }
  }

  _setClasses() {
    this._classes = {
      'mdc-button--minimal': this.size === this.SizeChoices.MINIMAL,
      'mdc-button--large': this.size === this.SizeChoices.LARGE,
      'mdc-button--massive': this.size === this.SizeChoices.MASSIVE,
      'mdc-button--icon': this.size === this.SizeChoices.ICON,

      'mdc-button--red': this.colorMode === this.ColorChoices.RED,
      'mdc-button--green': this.colorMode === this.ColorChoices.GREEN,
      'mdc-button--white-green': this.colorMode === this.ColorChoices.WHITEGREEN,
      'mdc-button--blue': this.colorMode === this.ColorChoices.BLUE,
      'mdc-button--white-blue': this.colorMode === this.ColorChoices.WHITEBLUE,
      'mdc-button--gray': this.colorMode === this.ColorChoices.GRAY,
      'mdc-button--white-red': this.colorMode === this.ColorChoices.WHITERED,

      'mdc-button--thick-border': this.thickBorder && !this.noBorder,
      'mdc-button--no-border': this.noBorder
    };
  }

  _setAttrs() {
    if (this.linkRel != null) {
      this._buttonElem.setAttribute('rel', this.linkRel);
    }
  }

  static componentConfig(controller) {
    return {
      templateUrl: buttonTemplateUrl,
      controller: controller,
      bindings: {
        icon: '@?',
        light: '<',
        highlight: '<',
        isDisabled: '<?',
        isWarning: '<?',
        isFaded: '<?',
        error: '<?',
        type: '@?',
        pixelSize: '<?',
        thickBorder: '<?',
        noBorder: '<?',
        clickAction: '&?',
        link: '@?',
        linkRel: '@?',
        target: '@?',
        label: '@?'
      },
      transclude: true,
    };
  }
}

function buttonTemplateUrl($element, $attrs) {
  'ngInject';
  if ($attrs.link != null) {
    return linkTpl;
  } else {
    return tpl;
  }
}


export {
  MDCButtonComponent,
  SIZE_CHOICES, COLOR_CHOICES,
  getColor
};

